import { ResponsiveBar } from "@nivo/bar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import type { TeamCompletion } from "@/hooks/use-dashboard";

interface TeamCompletionChartProps {
  data: TeamCompletion[];
}

function getBarColor(value: number): string {
  if (value >= 75) return "hsl(40, 63%, 48%)";     // Gold
  if (value >= 50) return "hsl(213, 52%, 63%)";    // Bright Blue
  if (value >= 25) return "hsl(228, 65%, 19%)";    // Navy
  return "hsl(4, 64%, 46%)";                        // Red
}

export function TeamCompletionChart({ data }: TeamCompletionChartProps) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Completion</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="No team data"
            description="Team member completion data will appear here."
          />
        </CardContent>
      </Card>
    );
  }

  // Sort descending by completion
  const sorted = [...data].sort((a, b) => b.completion_percent - a.completion_percent);

  const chartData = sorted.map((m) => ({
    name: m.user_name.split(" ")[0] ?? m.user_name,
    fullName: m.user_name,
    completion: Math.round(m.completion_percent),
    totalKrs: m.total_key_results,
    color: getBarColor(m.completion_percent),
  }));

  // Use horizontal bars when many members for readability
  const isHorizontal = data.length > 6;
  const chartHeight = isHorizontal ? Math.max(240, data.length * 40) : 280;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Completion</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height: chartHeight }}>
          <ResponsiveBar
            data={chartData}
            keys={["completion"]}
            indexBy="name"
            layout={isHorizontal ? "horizontal" : "vertical"}
            margin={
              isHorizontal
                ? { top: 10, right: 40, bottom: 20, left: 100 }
                : { top: 10, right: 20, bottom: 50, left: 50 }
            }
            padding={0.35}
            valueScale={{ type: "linear", min: 0, max: 100 }}
            colors={({ data: d }) => d.color as string}
            borderRadius={4}
            axisLeft={
              isHorizontal
                ? { tickSize: 0, tickPadding: 8 }
                : { tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }
            }
            axisBottom={
              isHorizontal
                ? { tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }
                : { tickSize: 0, tickPadding: 8, tickRotation: data.length > 8 ? -45 : 0 }
            }
            enableGridX={isHorizontal}
            enableGridY={!isHorizontal}
            gridYValues={[0, 25, 50, 75, 100]}
            enableLabel={true}
            label={(d) => `${d.value}%`}
            labelSkipWidth={24}
            labelSkipHeight={16}
            labelTextColor="hsl(0, 0%, 100%)"
            tooltip={({ data: d }) => (
              <div className="rounded-md bg-[hsl(var(--popover))] px-3 py-2 text-sm shadow-md border">
                <p className="font-semibold">{d.fullName}</p>
                <p>{d.completion}% complete</p>
                <p className="text-[hsl(var(--muted-foreground))]">{d.totalKrs} key results</p>
              </div>
            )}
            theme={{
              text: { fontFamily: "Source Sans 3, sans-serif", fill: "hsl(228, 65%, 19%)" },
              grid: { line: { stroke: "hsl(200, 18%, 85%)", strokeWidth: 1 } },
              axis: { ticks: { text: { fill: "hsl(228, 20%, 46%)" } } },
            }}
            role="img"
            ariaLabel="Team completion chart"
          />
        </div>
      </CardContent>
    </Card>
  );
}
